import {guidGenerator} from '../Functions/GuidGenerator';

/**
 * Request to the article buy api
 */
export interface ArticleBuyRequest {
    items: {
        [itemId: string]: {
            itemId: number,
            quantities: number[],
            store: boolean,
        }
    }
}

/**
 * Response from the article buy api
 */
export interface ArticleBuyResponse {
    items: {
        [itemId: string]: {
            isForSale: boolean,
            isFree: boolean,
            isOnRequest: boolean,
            itemId: string,
            maximumQuantity: number,
            minimumQuantity: number,
            pricePrefix: string|null,
            prices: {
                [quantity: number]: {
                    amount: string,
                    discountPercentage: string,
                    discountValue: string,
                    isActionPrice: boolean,
                    isDiscountable: boolean,
                    itemId: string,
                    originalAmount: string,
                    originalUnitPrice: string,
                    quantity: number,
                    quantityDiscounts: any[],
                    unitDiscountValue: string,
                    unitPrice: string,
                }
            },
            showSavingsAsPercentage: boolean,
        }
    }
}

/**
 * Custom event interface for the action buy event
 */
export interface ActionBuyEventInterface extends CustomEvent {
    detail: {
        itemId: string,
        article: ArticleBuyResponse['items'][string],
        price: ArticleBuyResponse['items'][string]['prices'][number],
    }
}

export class ArticleBuy {
    protected combinedIdToElementsIdx = new Map();
    protected elementIdToElementIdx = new Map();
    protected elements: NodeListOf<HTMLElement> = null;
    protected wrapper: HTMLElement = null;

    constructor(elements: NodeListOf<HTMLElement>, wrapper: HTMLElement = null) {
        this.elements = elements;
        this.wrapper = wrapper;

        this.init();
    }

    protected init() {
        const request = {
            items: {}
        };

        if (this.elements.length === 0) {
            return;
        }

        window.addEventListener('buy-data-quantity-change', (e: CustomEvent) => {
            this.update(e.detail.itemId, e.detail.quantity, e.detail.updateElements, e.detail.callback);
        });

        this.elements.forEach((element, idx) => {
            if (element.dataset.articleBuyInitialized) {
                return;
            }

            const guid = guidGenerator();
            element.dataset.articleBuyInitialized = guid;
            this.elementIdToElementIdx.set(guid, idx);

            // get id from element
            let id = element.dataset.articleBuyItemId;
            if (!id) {
                return;
            }

            let quantity = parseInt(element.dataset.articleBuyItemQuantity ?? '');
            if (!quantity) {
                quantity = 1;
            }

            if (!request.items.hasOwnProperty(id)) {
                request.items[id] = {
                    itemId: id,
                    quantities: [],
                    store: false,
                };
            }

            // map combination of id and quantity to element index
            const combinedId = id + '-' + quantity;
            if (!this.combinedIdToElementsIdx.has(combinedId)) {
                this.combinedIdToElementsIdx.set(combinedId, []);
            }

            this.combinedIdToElementsIdx.get(combinedId).push(idx);

            if (!request['items'][id]['store']) {
                request['items'][id]['store'] = !!element.dataset.articleBuyStore ?? false;
            }

            // add quantity to request
            if (!request['items'][id]['quantities'].includes(quantity)) {
                request['items'][id]['quantities'].push(quantity);
            }
        });

        if (Object.keys(request.items).length === 0) {
            return;
        }

        this.query(request);
    }

    /**
     * Update the quantity of an item
     *
     * @param itemId
     * @param quantity
     * @param elements
     * @param callback
     * @protected
     */
    public async update(itemId: string, quantity: number, elements: NodeListOf<HTMLElement>, callback: Function = undefined) {
        fetch(
            '/api/v1/buy',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    items: {
                        [itemId]: {
                            itemId: itemId,
                            updateStored: true,
                            quantities: [quantity],
                        },
                    },
                }),
            }
        ).then(response => response.json())
        .then((data: ArticleBuyResponse) => {
            const event = new CustomEvent('Plandent.ArticleBuy.DataLoaded', {
                detail: {
                    itemId: itemId,
                    article: data.items[itemId],
                    price: data.items[itemId].prices[quantity],
                },
            } as ActionBuyEventInterface);

            elements.forEach((element, id) => {
                const idx = this.elementIdToElementIdx.get(id);
                this.elements[idx].dispatchEvent(event);
            });

            if (callback !== undefined) {
                callback();
            }
        })
        .catch((error) => {
            console.error('Error loading data from article buy api:', error);

            elements.forEach((element, id) => {
                const idx = this.elementIdToElementIdx.get(id);
                this.elements[idx].dispatchEvent(new CustomEvent('Plandent.ArticleBuy.Failed', {
                    detail: {
                        error: error,
                        method: 'update'
                    },
                }));
            });
        });
    }

    /**
     * Query the article buy api
     *
     * @param request
     * @protected
     */
    protected async query(request: ArticleBuyRequest) {
        fetch(
            '/api/v1/buy',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(request),
            }
        ).then(response => response.json())
        .then((data: ArticleBuyResponse) => {
            Object.keys(data.items).forEach((itemId) => {
                const event = new CustomEvent('Plandent.ArticleBuy.DataLoaded', {
                    detail: {
                        itemId: itemId,
                        article: data.items[itemId],
                        price: undefined,
                    },
                } as ActionBuyEventInterface);

                if (!data.items[itemId].prices) {
                    console.warn('no prices found for item "' + itemId + '"');
                }

                request['items'][itemId].quantities.forEach((quantity) => {
                    const combinedId = itemId + '-' + quantity;

                    if (data.items[itemId]['prices']) {
                        event.detail.price = data.items[itemId]['prices'][quantity];
                    }

                    if (this.combinedIdToElementsIdx.has(combinedId)) {
                        this.combinedIdToElementsIdx.get(combinedId).forEach((idx) => {
                            this.elements[idx].dispatchEvent(event);
                        });
                    }
                })
            })
        })
        .then(() => {
            const event = new CustomEvent('Plandent.ArticleBuy.DataLoadedAll');
            document.dispatchEvent(event);

            if (this.wrapper) {
                this.wrapper.dispatchEvent(event);
            }
        })
        .catch((error) => {
            console.error('Error loading data from article buy api:', error);

            const errorEvent = new CustomEvent('Plandent.ArticleBuy.Failed', {
                detail: {
                    error: error,
                    method: 'query'
                },
            });

            this.combinedIdToElementsIdx.forEach((idxs) => {
                idxs.forEach((idx) => {
                    this.elements[idx].dispatchEvent(errorEvent);
                });
            });

            if (this.wrapper) {
                const errorEvent = new CustomEvent('Plandent.ArticleBuy.FailedAll', {
                    detail: {
                        error: error,
                        method: 'query'
                    },
                });
                this.wrapper.dispatchEvent(errorEvent);
            }
        });
    }
}

/**
 * Initialize the article buy
 *
 * @param elements
 * @param wrapper
 */
export function initArticleBuy(elements: NodeListOf<HTMLElement>, wrapper: HTMLElement = null) {
    return new ArticleBuy(elements, wrapper);
}
